import { useCallback } from "react";
import dayjs from "dayjs";
import { Button, Checkbox, DatePicker, Input, Segmented, Typography } from "antd";
import { CONSENT_CLAUSES } from "../../utils/deliveryConsent";
import { useTranslation } from "../../i18n";

const { Paragraph } = Typography;

const PRESET_DAYS = [7, 14, 30, 90];

function VideoReviewFormFields({ values, onChange, showPathHint = true }) {
    const { t } = useTranslation();

    const disabledDate = useCallback((current) => {
        return current && current < dayjs().startOf("day");
    }, []);

    const disabledDateTime = useCallback((current) => {
        if (!current || !current.isSame(dayjs(), "day")) {
            return {};
        }
        const now = dayjs();
        return {
            disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
            disabledMinutes: (hour) => {
                if (hour > now.hour()) {
                    return [];
                }
                if (hour < now.hour()) {
                    return Array.from({ length: 60 }, (_, i) => i);
                }
                return Array.from({ length: now.minute() }, (_, i) => i);
            },
            disabledSeconds: (hour, minute) => {
                if (hour > now.hour() || minute > now.minute()) {
                    return [];
                }
                if (hour < now.hour() || minute < now.minute()) {
                    return Array.from({ length: 60 }, (_, i) => i);
                }
                return Array.from({ length: now.second() + 1 }, (_, i) => i);
            },
        };
    }, []);

    const applyPreset = useCallback(
        (days) => {
            onChange(
                "expiry",
                dayjs().add(days, "day").hour(23).minute(59).second(59).millisecond(0)
            );
        },
        [onChange]
    );

    return (
        <>
            <div className="delivery-admin-field">
                <label htmlFor="video-review-name">{t("videoReview.admin.videoNameLabel")}</label>
                <Input
                    id="video-review-name"
                    placeholder={t("videoReview.admin.videoNamePlaceholder")}
                    value={values.videoName}
                    onChange={(e) => onChange("videoName", e.target.value)}
                />
                <Paragraph
                    type="secondary"
                    style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                >
                    {t("videoReview.admin.videoNameHint")}
                </Paragraph>
            </div>

            <div className="delivery-admin-field">
                <label htmlFor="video-review-path">{t("videoReview.admin.pathLabel")}</label>
                <Input
                    id="video-review-path"
                    placeholder={t("videoReview.admin.pathPlaceholder")}
                    value={values.videoPath}
                    onChange={(e) => onChange("videoPath", e.target.value)}
                />
                {showPathHint && (
                    <Paragraph
                        type="secondary"
                        style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                    >
                        {t("videoReview.admin.pathHint")}
                    </Paragraph>
                )}
            </div>

            <div className="delivery-admin-field">
                <label>{t("videoReview.admin.expiryLabel")}</label>
                <div className="delivery-admin-presets">
                    {PRESET_DAYS.map((days) => (
                        <Button key={days} onClick={() => applyPreset(days)}>
                            {t("common.days", { count: days })}
                        </Button>
                    ))}
                </div>
                <DatePicker
                    showTime={{ format: "HH:mm:ss" }}
                    format="YYYY-MM-DD HH:mm:ss"
                    value={values.expiry}
                    onChange={(value) => value && onChange("expiry", value)}
                    disabledDate={disabledDate}
                    disabledTime={disabledDateTime}
                    style={{ width: "100%" }}
                    inputReadOnly
                />
            </div>

            <div className="delivery-admin-field">
                <label>{t("delivery.admin.consentLabel")}</label>
                <Checkbox.Group
                    className="delivery-admin-consent-group"
                    value={values.consentClauses || []}
                    onChange={(value) => onChange("consentClauses", value)}
                >
                    {CONSENT_CLAUSES.map((clause) => (
                        <Checkbox key={clause.id} value={clause.id}>
                            {clause.kind === "social"
                                ? t("videoReview.consent.clauses.socialLabel")
                                : t(`videoReview.consent.clauses.${clause.key}`)}
                        </Checkbox>
                    ))}
                </Checkbox.Group>
                <Paragraph
                    type="secondary"
                    style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                >
                    {t("videoReview.admin.consentHint")}
                </Paragraph>
            </div>

            <div className="delivery-admin-field">
                <label>{t("videoReview.admin.visibilityLabel")}</label>
                <Segmented
                    block
                    value={values.commentVisibility}
                    onChange={(value) => onChange("commentVisibility", value)}
                    options={[
                        { label: t("videoReview.admin.visibilityShared"), value: "shared" },
                        { label: t("videoReview.admin.visibilityIndividual"), value: "individual" },
                    ]}
                />
                <Paragraph
                    type="secondary"
                    style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                >
                    {values.commentVisibility === "individual"
                        ? t("videoReview.admin.visibilityIndividualHint")
                        : t("videoReview.admin.visibilitySharedHint")}
                </Paragraph>
            </div>
        </>
    );
}

export default VideoReviewFormFields;
